import { ChangeEvent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { DynamicDropdown } from 'Components/molecules';
import { ConfirmationModal } from 'Components/molecules';
import { SHARE_SECURITY_POSTURE_DATA } from './shareSecurityPostureData';
import { parseMultipleDevicesSettings, withMultipleDevicesEnabled } from './multipleDevicesSetting';
import {
    BUTTON_LABEL_MAP,
    MODAL_CONTENT_MAP,
    SUCCESS_VARIANT,
    CONNECTOR_SETTINGS_SAVED,
    HEALTH_SCORE_CONFIGURATION,
    HEALTH_SCORE_CONFIGURATION_TABS,
    EARLY_ACCESS_ASSISTANT_TIER,
    SETTINGS_PAGE_TITLE,
    SETTINGS_PAGE_SUBTITLE,
    SETTINGS_SAVED_SUCCESS,
    SETTINGS_SAVED_ERROR,
    SETTINGS_TAB_GENERAL,
    SETTINGS_TAB_USERS,
    SETTINGS_TAB_HEALTH,
    SETTINGS_TAB_EARLY_ACCESS,
    SETTINGS_TAB_MCP,
    SETTINGS_NAV_USERS_LINK,
    SETTINGS_NAV_HEALTH_TITLE,
    SETTINGS_NAV_HEALTH_LINK,
    SETTINGS_NAV_EA_LINK,
    SETTINGS_ENGAGEMENT_WINDOW_TITLE,
    SETTINGS_ENGAGEMENT_WINDOW_SUBTITLE,
    SETTINGS_WELCOME_DELAY_LABEL,
    SETTINGS_WELCOME_DELAY_SUBLABEL,
    SETTINGS_ACTIVE_DAYS_LABEL,
    SETTINGS_ACTIVE_DAYS_SUBLABEL,
    SETTINGS_ACTIVE_HOURS_LABEL,
    SETTINGS_ACTIVE_HOURS_SUBLABEL,
    SETTINGS_START_TIME_PLACEHOLDER,
    SETTINGS_END_TIME_PLACEHOLDER,
    SETTINGS_TIMEZONE_LABEL,
    SETTINGS_TIMEZONE_SUBLABEL,
    SETTINGS_REMINDER_RANGE_LABEL,
    SETTINGS_REMINDER_RANGE_SUBLABEL,
    SETTINGS_NOTIFICATIONS_TITLE,
    SETTINGS_NOTIFICATIONS_SUBTITLE,
    SETTINGS_ENABLE_NOTIFICATIONS_LABEL,
    SETTINGS_NOTIFICATIONS_ON_STATUS,
    SETTINGS_NOTIFICATIONS_OFF_STATUS,
    SETTINGS_ASSET_MGMT_SUBTITLE,
    SETTINGS_ASSET_LAST_SEEN_SUBLABEL,
    SETTINGS_IDLE_THRESHOLD_LABEL,
    SETTINGS_IDLE_THRESHOLD_SUBLABEL,
    SETTINGS_MULTIPLE_DEVICES_LABEL,
    SETTINGS_MULTIPLE_DEVICES_SUBLABEL,
    SETTINGS_USERS_TITLE,
    SETTINGS_USERS_SUBTITLE,
    SETTINGS_USERS_STAT_TOTAL,
    SETTINGS_USERS_STAT_TOTAL_SUB,
    SETTINGS_USERS_STAT_ACTIVE,
    SETTINGS_USERS_STAT_ACTIVE_SUB,
    SETTINGS_USERS_STAT_PENDING,
    SETTINGS_USERS_STAT_PENDING_SUB,
    SETTINGS_USERS_STAT_ADMINS,
    SETTINGS_USERS_STAT_ADMINS_SUB,
    SETTINGS_HEALTH_SUBTITLE,
    SETTINGS_HEALTH_INFO_BANNER,
    SETTINGS_EA_TITLE,
    SETTINGS_EA_SUBTITLE,
    SETTINGS_EA_INFO_BANNER,
    SETTINGS_EA_PLATFORM_TITLE,
    SETTINGS_EA_PLATFORM_SUBTITLE,
    SETTINGS_EA_AMPY_TITLE,
    SETTINGS_EA_AMPY_SUBTITLE,
    SETTINGS_EA_TIER_LABEL,
    SETTINGS_EA_TIER_DESC_BETA,
    SETTINGS_EA_TIER_DESC_EXPERIMENTAL,
    SETTINGS_EA_TIER_DESC_STABLE,
    SETTINGS_EA_PLATFORM_FEATURE_1,
    SETTINGS_EA_PLATFORM_FEATURE_2,
    SETTINGS_EA_PLATFORM_FEATURE_3,
    SETTINGS_EA_AMPY_FEATURE_1,
    SETTINGS_EA_AMPY_FEATURE_2,
    SETTINGS_EA_AMPY_FEATURE_3,
} from 'Core-utils/constants/constants';
import {
    AMP_SETTINGS,
    AgentDto,
    AgentStatus,
    AuthRole,
    CustomScoreCohortDto,
    FindingSpecDto,
    KIND,
    MultipleDevicesFindingSettings,
} from '@ampsec/platform-client';
import { useApiData } from 'Hooks/useApiData';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import {
    createSetting,
    getTenantSettings,
    updateSettings,
    updateAllSettings,
    getAgentsList,
    getFindingSpecsPage,
    getHighRiskCohorts,
} from 'Apis/library';
import { useFetchAllPages } from 'Components/organisms/AgGridBase';
import { AmpSettingDto, AmpSettingUpsertDto } from '@ampsec/platform-client';
import { Dispatch, RootState } from 'Rdx/store';
import { useDispatch, useSelector } from 'react-redux';
import PageErrorHandlerAndLoaderWrapper from 'Wrappers/PageErrorHandlerAndLoaderWrapper';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { ConfigurationTabs } from 'Components/organisms';
import HighRiskCohortConfigurationTable, {
    type HighRiskCohortConfigurationTableHandle,
} from './HighRiskCohortConfigurationTable';
import HealthScoreConfigurationTable from './HealthScoreConfigurationTable';
import { IoMdInformationCircleOutline } from '@react-icons/all-files/io/IoMdInformationCircleOutline';
import { useI18n } from 'Hooks/useI18n';
import { WeekDayPicker } from 'Components/molecules/WeekDayPicker';
import EventTimeZoneSetting from 'Components/molecules/EventTimeZoneSetting/EventTimeZoneSetting';
import { generateDayOptions, generateTimeSlots } from 'Core-utils/helpers/helpers';
import './styles.scss';
import UserManagementGrid from './UserManagementGrid';
import { SsoSetupSection } from './SsoSetupSection';
import { McpAccessSection } from './McpAccessSection';
import { RiGroupLine } from '@react-icons/all-files/ri/RiGroupLine';
import { RiCheckboxCircleFill } from '@react-icons/all-files/ri/RiCheckboxCircleFill';
import { RiShieldLine } from '@react-icons/all-files/ri/RiShieldLine';
import { RiRocketLine } from '@react-icons/all-files/ri/RiRocketLine';
import { RiUserLine } from '@react-icons/all-files/ri/RiUserLine';
import { RECONCILIATION_ASSET_SOURCE_OF_TRUTH_SETTING_KEY } from './assetAttributionSource';
import { isMcpAccessEnabled } from './mcpAccessFlag';

export type TenantAmpSetting = '' | keyof typeof AMP_SETTINGS.tenant;

type SettingsTab = 'general' | 'users' | 'health' | 'early' | 'mcp';

/** Stable module-level component so SWR refreshes do not remount health sub-tabs. */
const HIGH_RISK_COHORT_CONFIG_TAB = HEALTH_SCORE_CONFIGURATION_TABS[1];

function SettingsHealthTab({
    activeConfigTab,
    onConfigTabChange,
}: {
    activeConfigTab: string;
    onConfigTabChange: (eventKey: string) => void;
}) {
    const { t } = useI18n();
    const cohortTableRef = useRef<HighRiskCohortConfigurationTableHandle>(null);

    const configTabs = useMemo(
        () => [
            {
                eventKey: HEALTH_SCORE_CONFIGURATION_TABS[0],
                title: HEALTH_SCORE_CONFIGURATION_TABS[0],
                content: <HealthScoreConfigurationTable />,
            },
            {
                eventKey: HIGH_RISK_COHORT_CONFIG_TAB,
                title: HIGH_RISK_COHORT_CONFIG_TAB,
                content: <HighRiskCohortConfigurationTable ref={cohortTableRef} />,
            },
        ],
        [],
    );

    const handleBeforeConfigTabChange = async (previousKey: string, nextKey: string) => {
        if (previousKey === HIGH_RISK_COHORT_CONFIG_TAB && nextKey !== HIGH_RISK_COHORT_CONFIG_TAB) {
            await cohortTableRef.current?.flushPendingCohorts();
        }
    };

    return (
        <div>
            <div className="settings-v2__section-header">
                <div className="settings-v2__section-icon">
                    <RiShieldLine size={24} />
                </div>
                <div>
                    <h2 className="settings-v2__section-title">
                        {t(HEALTH_SCORE_CONFIGURATION, 'Health Score Configuration')}
                    </h2>
                    <p className="settings-v2__section-subtitle">
                        {t(
                            SETTINGS_HEALTH_SUBTITLE,
                            'Set deduction weights for security findings. Scores update automatically every hour.',
                        )}
                    </p>
                </div>
            </div>

            <div className="settings-v2__info-banner">
                <IoMdInformationCircleOutline size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                {t(
                    SETTINGS_HEALTH_INFO_BANNER,
                    'Scores are saved automatically and will be reflected within the next hour across all dashboards.',
                )}
            </div>

            <ConfigurationTabs
                configTabs={configTabs}
                activeKey={activeConfigTab}
                onActiveKeyChange={onConfigTabChange}
                onBeforeTabChange={handleBeforeConfigTabChange}
            />
        </div>
    );
}

const REMINDER_ANCHOR_RANGE_SETTING = (AMP_SETTINGS.tenant as any).REMINDER_OFFICE_HOURS_ANCHOR_RANGE_MINUTES ?? {
    id: 'engagement.reminder.anchor.range.minutes',
    defaultValue: 120,
};

const SettingConfirmationModal = ({
    content,
    getAmpSettingValue,
    savingSettings,
    settingKey,
    showConfirmationModal,
    title,
    onCallback,
    onClose,
    onConfirm,
    onError,
}: {
    content?: string;
    getAmpSettingValue: (settingKey: string) => any;
    savingSettings?: boolean;
    settingKey: string;
    showConfirmationModal: boolean;
    title: string;
    onCallback?: () => void;
    onClose?: (newAmpSetting?: AmpSettingDto | AmpSettingDto[]) => void;
    onConfirm?: () => Promise<AmpSettingDto | AmpSettingDto[] | undefined>;
    onError?: (error: string) => void;
}) => {
    const { t } = useI18n();
    const handleConfirmAction = () => {
        if (settingKey === '') {
            return;
        }
        onConfirm &&
            onConfirm()
                .then((data) => onClose && onClose(data))
                .catch((error) => onError && onError(error.message))
                .finally(() => onCallback && onCallback());
    };

    const renderPrimaryButtonLabel = () => {
        if (settingKey === 'notifications.enabled') {
            return (
                BUTTON_LABEL_MAP.NOTIFICATIONS_ENABLED?.[
                    getAmpSettingValue(AMP_SETTINGS.tenant.NOTIFICATIONS_ENABLED.id)?.toString()
                ] || ''
            );
        }
        if (settingKey === 'engagement.earlyaccess.enabled') {
            return getAmpSettingValue(AMP_SETTINGS.tenant.ENGAGEMENTS_EARLY_ACCESS_ENABLED.id)
                ? t('DISABLE_ENGAGEMENTS_EARLY_ACCESS_CONFIRM_YES')
                : t('ENABLE_ENGAGEMENTS_EARLY_ACCESS_CONFIRM_YES');
        }
        return 'Yes, Save it';
    };

    const handleExit = () => onClose && onClose(undefined);

    return (
        <ConfirmationModal
            cancelButtonDisabled={savingSettings}
            modalTitle={title}
            modalBodyTitle={content}
            onPrimaryButtonClick={handleConfirmAction}
            primaryButtonLabel={savingSettings ? 'Saving…' : renderPrimaryButtonLabel()}
            primaryButtonDisabled={savingSettings}
            loading={savingSettings}
            show={showConfirmationModal}
            onClose={handleExit}
            buttonVariant="primary"
        />
    );
};

const FINDING_SPECS_BASE_PARAMS = {
    'sort[score]': 'DESC',
    'sort[displayValue]': 'ASC',
    excludePreset: 'healthScoreConfiguration',
} as const;

const fetchFindingSpecsPage = (params: typeof FINDING_SPECS_BASE_PARAMS & { offset: number; limit: number }) =>
    getFindingSpecsPage(KIND.FINDING_SPECS, params);

const SettingsPage = () => {
    const { t } = useI18n();
    const {
        data: tenantSettings,
        isLoading,
        refresh: refreshTenantSettings,
        error: settingsError,
    } = useApiData<{ [key: string]: AmpSettingDto }, { [key: string]: AmpSettingDto }>(
        'TENANT_SETTINGS',
        getTenantSettings,
        (a) => a,
    );

    const [error, setError] = useState<string>('');
    const enableBanner = useSelector((state: RootState) => state.featureModel.enableBanner);
    const [settingKey, setSettingKey] = useState<string | null>();
    const [savingSettings, setSavingSettings] = useState<boolean>(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const { addToast } = useContext(ToastsContext);
    const [_initialSettingsValues, setInitialSettingsValues] = useState<{ [key: string]: AmpSettingDto } | null>();
    const [userSettingInput, setUserSettingInput] = useState<{ [key: string]: string | boolean }>({});
    const [userData, setUserData] = useState(SHARE_SECURITY_POSTURE_DATA);
    const [showModal, setShowModal] = useState({ accessUser: false, removeUser: false });

    const dispatch = useDispatch<Dispatch>();
    const [assetArchivedThreshold, setAssetArchivedThreshold] = useState<string>(
        AMP_SETTINGS.tenant.ASSET_ARCHIVED_THRESHOLD.defaultValue,
    );
    const [ampyEarlyAccessEnabled, setAmpyEarlyAccessEnabled] = useState<boolean>(false);
    const [earlyAccessEnabled, setEarlyAccessEnabled] = useState<boolean>(false);
    const [selectedEarlyAccessTier, setSelectedEarlyAccessTier] = useState<string>(EARLY_ACCESS_ASSISTANT_TIER.STABLE);
    const [selectedAmpyEarlyAccessTier, setSelectedAmpyEarlyAccessTier] = useState<string>(
        EARLY_ACCESS_ASSISTANT_TIER.STABLE,
    );
    const [assetLastSeenThreshold, setAssetLastSeenThreshold] = useState<string>(
        AMP_SETTINGS.tenant.ASSET_LAST_SEEN_THRESHOLD.defaultValue,
    );
    // APD-1880 — the whole settings object is stored, not just the flag, so the
    // toggle can write `enabled` back without dropping the tenant's threshold.
    const [multipleDevicesSettings, setMultipleDevicesSettings] = useState<MultipleDevicesFindingSettings>(
        AMP_SETTINGS.tenant.MULTIPLE_DEVICES_SETTINGS.defaultValue,
    );
    const [selectedDelay, setSelectedDelay] = useState<string>(AMP_SETTINGS.tenant.WELCOME_MESSAGE_DELAY.defaultValue);
    const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>(
        AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_SELECTED_WEEKDAYS?.defaultValue,
    );
    const [selectedTimezone, setSelectedTimezone] = useState<string>(
        AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_TIMEZONE?.defaultValue,
    );
    const [startTime, setStartTime] = useState(AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_START_TIME?.defaultValue);
    const [endTime, setEndTime] = useState(AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_END_TIME?.defaultValue);
    const [reminderAnchorRangeMinutes, setReminderAnchorRangeMinutes] = useState<string>(
        REMINDER_ANCHOR_RANGE_SETTING.defaultValue.toString(),
    );

    const welcomeMessageDropdownOptions = [
        'Immediate',
        '1 minutes',
        '2 minutes',
        '3 minutes',
        '4 minutes',
        '5 minutes',
        '6 minutes',
        '7 minutes',
        '8 minutes',
        '9 minutes',
        '10 minutes',
    ];

    useEffect(() => {
        if (!tenantSettings) {
            return;
        }
        setEarlyAccessEnabled(selectedEarlyAccessTier !== EARLY_ACCESS_ASSISTANT_TIER.STABLE);
    }, [selectedEarlyAccessTier, tenantSettings]);

    useEffect(() => {
        if (!tenantSettings) {
            return;
        }
        setAmpyEarlyAccessEnabled(selectedAmpyEarlyAccessTier !== EARLY_ACCESS_ASSISTANT_TIER.STABLE);
    }, [selectedAmpyEarlyAccessTier, tenantSettings]);

    const getAmpSettingValue = (key: string): any => {
        if (!key || (tenantSettings && Object.keys(tenantSettings).length === 0)) {
            return true;
        }
        return tenantSettings && (tenantSettings as any)[key]?.value;
    };

    const updateDependentAmpSettings = (settingId: string, value: any) => {
        if (settingId === AMP_SETTINGS.tenant.ENGAGEMENTS_EARLY_ACCESS_ENABLED.id) {
            const newValue = value ? EARLY_ACCESS_ASSISTANT_TIER.BETA : EARLY_ACCESS_ASSISTANT_TIER.STABLE;
            setUserSettingInput((current) => ({
                ...current,
                [AMP_SETTINGS.tenant.GENERATIVE_AI_ASSISTANT_TIER.id]: newValue,
            }));
        }
    };

    const handleToggle = (type: string, value: boolean | string) => {
        setShowConfirmationModal((visible) => !visible);
        setSettingKey(type);
        setUserSettingInput((current) => ({ ...current, [type]: value }));
        updateDependentAmpSettings(type, value);
    };

    const handleMoreActions = (item: string, id: string) => {
        if (item === 'REMOVE USER') {
            setShowModal({ accessUser: false, removeUser: true });
            setUserData(userData.filter((user) => user.id.toString() !== id));
        } else {
            const newStatus = item === 'ACTIVATE USER' ? 'ACTIVE' : 'SUSPENDED';
            setUserData(
                userData.map((user) => {
                    if (user.id.toString() === id) {
                        user.status = newStatus;
                    }
                    return user;
                }),
            );
        }
    };

    const handleUpdateAccess = (id: number, value: string) => {
        setShowModal({ accessUser: true, removeUser: false });
        setUserData(
            userData.map((user) => {
                if (user.id === id) {
                    user.access = value;
                }
                return user;
            }),
        );
    };

    const handleCloseModal = () => setShowModal({ accessUser: false, removeUser: false });
    const handleAccessChange = () => {
        handleUpdateAccess(0, '');
        setShowModal({ accessUser: false, removeUser: false });
    };
    const handleRemoveUser = () => {
        handleMoreActions('', '');
        setShowModal({ accessUser: false, removeUser: false });
    };

    const handleTenantList = () => {
        if (isLoading || !tenantSettings || Object.keys(tenantSettings).length === 0) {
            return;
        }
        if (!Object.keys(tenantSettings)?.includes(AMP_SETTINGS.tenant.NOTIFICATIONS_ENABLED.id)) {
            const notificationsEnabledSetting = (tenantSettings as any)[AMP_SETTINGS.tenant.NOTIFICATIONS_ENABLED.id];
            if (notificationsEnabledSetting === undefined || notificationsEnabledSetting === null) {
                createSetting(
                    AMP_SETTINGS.tenant.NOTIFICATIONS_ENABLED.id,
                    AMP_SETTINGS.tenant.NOTIFICATIONS_ENABLED.defaultValue,
                ).catch((err) => setError(err.message));
            }
        }
    };

    const handleToggleEarlyAccessEnabled = (ev: ChangeEvent<HTMLInputElement>) => {
        if (!ev.target.checked) {
            handleToggle(AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.id, EARLY_ACCESS_ASSISTANT_TIER.STABLE);
        } else {
            handleToggle(AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.id, EARLY_ACCESS_ASSISTANT_TIER.BETA);
        }
    };

    const handleToggleAmpyEarlyAccessEnabled = (ev: ChangeEvent<HTMLInputElement>) => {
        if (!ev.target.checked) {
            handleToggle(AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.id, EARLY_ACCESS_ASSISTANT_TIER.STABLE);
        } else {
            handleToggle(AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.id, EARLY_ACCESS_ASSISTANT_TIER.BETA);
        }
    };

    const handleModalClose = (newAmpSetting?: AmpSettingDto | AmpSettingDto[]) => {
        if (newAmpSetting) {
            addToast({ severity: SUCCESS_VARIANT, body: `${CONNECTOR_SETTINGS_SAVED}` });
        }
        setSettingKey(null);
        setShowConfirmationModal(false);
    };

    const handleModalError = (err: string) => setError(err);

    const getAmpSettingId = (key: string) => {
        if (!key || tenantSettings === undefined || tenantSettings === null) {
            return '';
        }
        return ((tenantSettings as any)[key]?.id as string) || '';
    };

    const transformSettingValue = (key: string): any => {
        if (!key || !userSettingInput) {
            return '';
        }
        switch (key) {
            case AMP_SETTINGS.tenant.SLACK_QA_ALL_MESSAGES.id:
            case AMP_SETTINGS.tenant.NOTIFICATIONS_ENABLED.id:
            case AMP_SETTINGS.tenant.ENGAGEMENTS_EARLY_ACCESS_ENABLED.id:
            case AMP_SETTINGS.tenant.GENERATIVE_AI_ASSISTANT_TIER.id:
            case AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.id:
            case AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.id:
                return userSettingInput[key];
            case AMP_SETTINGS.tenant.DEFAULT_NOTIFICATION_CONNECTOR_ID.id:
                return { cid: userSettingInput[key], displayValue: 'Default Notification Connector ID' };
            default:
                return getAmpSettingValue(key);
        }
    };

    const handleConfirmModal = async () => {
        setSavingSettings(true);
        if (!settingKey) {
            throw new Error('No setting was changed');
        }
        const settingsToUpdate: { id: string; value: unknown }[] = [];
        const ampSettingId = getAmpSettingId(settingKey);
        const existingSetting = Object.keys(tenantSettings as any).find((s) => s === settingKey);
        if (existingSetting !== undefined) {
            settingsToUpdate.push({ id: ampSettingId, value: transformSettingValue(settingKey) });
        } else {
            await createSetting(settingKey, transformSettingValue(settingKey));
        }
        Object.keys(userSettingInput).forEach((key) => {
            if (key !== settingKey) {
                const dependentSettingId = getAmpSettingId(key);
                if (dependentSettingId) {
                    settingsToUpdate.push({ id: dependentSettingId, value: transformSettingValue(key) });
                }
            }
        });
        return updateAllSettings(settingsToUpdate);
    };

    const handleModalCallback = () => {
        refreshTenantSettings();
        setSavingSettings(false);
    };

    const modalContent = () => {
        switch (settingKey) {
            case AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.id:
                return (getAmpSettingValue(AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.id) ??
                    AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.defaultValue) !==
                    AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.defaultValue
                    ? t('MODAL_CONTENT_ENGAGEMENTS_DISABLE_EARLY_ACCESS')
                    : t('MODAL_CONTENT_ENGAGEMENTS_ENABLE_EARLY_ACCESS');
            case AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.id:
                return (getAmpSettingValue(AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.id) ??
                    AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.defaultValue) !==
                    AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.defaultValue
                    ? t('MODAL_CONTENT_ENGAGEMENTS_DISABLE_AMPY_EARLY_ACCESS')
                    : t('MODAL_CONTENT_ENGAGEMENTS_ENABLE_AMPY_EARLY_ACCESS');
            case AMP_SETTINGS.tenant.NOTIFICATIONS_ENABLED.id:
                return MODAL_CONTENT_MAP.NOTIFICATIONS_ENABLED?.[
                    getAmpSettingValue(AMP_SETTINGS.tenant.NOTIFICATIONS_ENABLED.id)?.toString()
                ];
            case AMP_SETTINGS.tenant.DEFAULT_NOTIFICATION_CONNECTOR_ID.id:
                return 'Are you sure you want to save this setting?';
            default:
                return `UNKNOWN SETTING KEY: ${settingKey}`;
        }
    };

    const modalTitle = () => {
        switch (settingKey) {
            case AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.id:
                return (getAmpSettingValue(AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.id) ??
                    AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.defaultValue) !==
                    AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.defaultValue
                    ? t('MODAL_TITLE_ENGAGEMENTS_DISABLE_EARLY_ACCESS')
                    : t('MODAL_TITLE_ENGAGEMENTS_ENABLE_EARLY_ACCESS');
            case AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.id:
                return (getAmpSettingValue(AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.id) ??
                    AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.defaultValue) !==
                    AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.defaultValue
                    ? t('MODAL_TITLE_ENGAGEMENTS_DISABLE_AMPY_EARLY_ACCESS')
                    : t('MODAL_TITLE_ENGAGEMENTS_ENABLE_AMPY_EARLY_ACCESS');
            case AMP_SETTINGS.tenant.NOTIFICATIONS_ENABLED.id:
                return 'Disable Engagements';
            case AMP_SETTINGS.tenant.SLACK_QA_ALL_MESSAGES.id:
                return 'Send all engagements to Slack';
            case AMP_SETTINGS.tenant.DEFAULT_NOTIFICATION_CONNECTOR_ID.id:
                return 'Default Connector';
            case RECONCILIATION_ASSET_SOURCE_OF_TRUTH_SETTING_KEY:
                return 'Save attribution source preference';
            default:
                return `UNKNOWN SETTING KEY: ${settingKey}`;
        }
    };

    useEffect(() => {
        handleTenantList();
    }, [tenantSettings, isLoading]);

    useEffect(() => {
        if (!tenantSettings) {
            return;
        }
        const tempInitialSettingsValues: { [key: string]: AmpSettingDto } = {};
        Object.keys(tenantSettings as { [key: string]: AmpSettingDto }).forEach((setting) => {
            tempInitialSettingsValues[setting] = (tenantSettings as any)[setting];
        });
        setInitialSettingsValues(tempInitialSettingsValues);
        dispatch.featureModel.setBanner(!error && !getAmpSettingValue(AMP_SETTINGS.tenant.NOTIFICATIONS_ENABLED.id));
    }, [tenantSettings]);

    // ─── SettingsContent ────────────────────────────────────────────────────────
    const SettingsContent = () => {
        const auth = useSelector((state: RootState) => state.auth);
        const [activeTab, setActiveTab] = useState<SettingsTab>('general');
        const [healthConfigSubTab, setHealthConfigSubTab] = useState<string>(HEALTH_SCORE_CONFIGURATION_TABS[0]);

        // ── Agent stats (shared across General and Users tabs) ──────────────────
        const { data: agentsList } = useApiObjectData<AgentDto[], AgentDto[]>(KIND.AGENTS, getAgentsList, (a) => a);
        const safeAgentsList = Array.isArray(agentsList) ? agentsList : [];
        const totalAgents = safeAgentsList.length;
        const activeAgentsCount = useMemo(
            () => safeAgentsList.filter((a: AgentDto) => a.status === AgentStatus.ACTIVE).length,
            [agentsList],
        );
        const pendingAgentsCount = useMemo(
            () => safeAgentsList.filter((a: AgentDto) => (a.status as string)?.toUpperCase() === 'PENDING').length,
            [agentsList],
        );
        const adminCount = useMemo(
            () => safeAgentsList.filter((a: AgentDto) => a.role === AuthRole.OWNER || a.role === AuthRole.ADMIN).length,
            [agentsList],
        );

        // ── Health score stats (for General tab nav card) ───────────────────────
        // useFetchAllPages fetches all pages (250/page) to get an accurate count
        // across the full dataset. The health score table fetches independently
        // with its own limit:250 single-page call for display purposes.
        const { rows: findingSpecsData, isLoading: isFindingSpecsLoading } = useFetchAllPages<
            FindingSpecDto,
            typeof FINDING_SPECS_BASE_PARAMS
        >({
            fetchPage: fetchFindingSpecsPage,
            baseParams: FINDING_SPECS_BASE_PARAMS,
            pageSize: 250,
            maxRows: 5000,
        });
        const nonZeroDeductionCount = useMemo(
            () => findingSpecsData.filter((s) => s?.score && s?.score > 0).length,
            [findingSpecsData],
        );

        // Filter params match exactly what HighRiskCohortConfigurationTable uses so SWR
        // deduplicates — only one API call is made regardless of tab visit order.
        const { data: highRiskCohortsData } = useApiObjectData<CustomScoreCohortDto[], CustomScoreCohortDto[]>(
            KIND.CUSTOM_SCORE_COHORTS,
            getHighRiskCohorts,
            (a) => a,
            { 'sort[multiplier]': 'DESC' },
        );
        const safeHighRiskCohorts = Array.isArray(highRiskCohortsData) ? highRiskCohortsData : [];
        const highRiskCohortCount = safeHighRiskCohorts.length;

        const isHidden = useMemo(() => (auth.user.role as unknown as AuthRole) !== AuthRole.OWNER, [auth]);

        // The customer-facing MCP surface is gated per tenant in amp-platform-api, which
        // 403s every MCP call for a tenant without the flag. Owner-only was the tab's only
        // gate, so every tenant's owner could issue credentials that could never be used.
        const isMcpTabHidden = useMemo(
            () => isHidden || !isMcpAccessEnabled(tenantSettings),
            [isHidden, tenantSettings],
        );

        // Falls back rather than reading `activeTab` directly, so a viewer sitting on the MCP
        // tab when a settings refresh flips the flag off lands on General instead of on a
        // blank content pane with no tab to leave it by. Derived, so no state to keep in sync.
        const effectiveTab: SettingsTab = activeTab === 'mcp' && isMcpTabHidden ? 'general' : activeTab;

        // ── Setting mutators (same logic as before) ──────────────────────────────
        const AmpSettingSetter = (settingId: string, value: any) => {
            switch (settingId) {
                case AMP_SETTINGS.tenant.ASSET_ARCHIVED_THRESHOLD.id:
                    setAssetArchivedThreshold(value as string);
                    break;
                case AMP_SETTINGS.tenant.ASSET_LAST_SEEN_THRESHOLD.id:
                    setAssetLastSeenThreshold(value as string);
                    break;
                case AMP_SETTINGS.tenant.MULTIPLE_DEVICES_SETTINGS.id:
                    setMultipleDevicesSettings(parseMultipleDevicesSettings(value));
                    break;
                case AMP_SETTINGS.tenant.WELCOME_MESSAGE_DELAY.id:
                    setSelectedDelay(value as string);
                    break;
                case AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.id:
                    setSelectedEarlyAccessTier(
                        value === EARLY_ACCESS_ASSISTANT_TIER.DEV
                            ? EARLY_ACCESS_ASSISTANT_TIER.DEV_USER_FRIENDLY
                            : value.toUpperCase(),
                    );
                    break;
                case AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.id:
                    setSelectedAmpyEarlyAccessTier(
                        value === EARLY_ACCESS_ASSISTANT_TIER.DEV
                            ? EARLY_ACCESS_ASSISTANT_TIER.DEV_USER_FRIENDLY
                            : value.toUpperCase(),
                    );
                    break;
                case AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_SELECTED_WEEKDAYS.id:
                    setSelectedWeekdays(value as number[]);
                    break;
                case AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_TIMEZONE.id:
                    setSelectedTimezone(value as string);
                    break;
                case AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_START_TIME.id:
                    setStartTime(value as string);
                    break;
                case AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_END_TIME.id:
                    setEndTime(value as string);
                    break;
                case REMINDER_ANCHOR_RANGE_SETTING.id:
                    setReminderAnchorRangeMinutes((value as number).toString());
                    break;
                default:
                    break;
            }
        };

        const updateAmpSetting = (settingId: string, value: any) => {
            let modifiedValue = value;
            if (
                settingId === AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.id ||
                settingId === AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.id
            ) {
                modifiedValue =
                    value.toLowerCase() === EARLY_ACCESS_ASSISTANT_TIER.DEV_USER_FRIENDLY.toLowerCase()
                        ? EARLY_ACCESS_ASSISTANT_TIER.DEV
                        : value.toUpperCase();
            } else if (settingId === AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_TIMEZONE.id) {
                modifiedValue = value.replace(/\(GMT[+-]\d{2}:\d{2}\)\s/, '');
            }
            if (settingId) {
                updateSettings(getAmpSettingId(settingId), {
                    id: getAmpSettingId(settingId),
                    key: settingId,
                    value: modifiedValue,
                } as AmpSettingUpsertDto)
                    .then(() => {
                        AmpSettingSetter(settingId, value);
                        addToast({
                            severity: SUCCESS_VARIANT,
                            body: t(SETTINGS_SAVED_SUCCESS, 'Setting saved successfully.'),
                        });
                    })
                    .catch((err) => {
                        setError(err.message);
                        addToast({
                            severity: 'error',
                            body: t(SETTINGS_SAVED_ERROR, 'Failed to save setting. Please try again.'),
                        });
                    });
            }
        };

        const handleDelayChange = (option: string) =>
            updateAmpSetting(AMP_SETTINGS.tenant.WELCOME_MESSAGE_DELAY.id, option);
        const handleEarlyAccessTierChange = (option: string) =>
            updateAmpSetting(AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.id, option);
        const handleAmpyEarlyAccessTierChange = (option: string) =>
            updateAmpSetting(AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.id, option);
        const handleAssetLastSeenThresholdChange = (option: string) =>
            updateAmpSetting(AMP_SETTINGS.tenant.ASSET_LAST_SEEN_THRESHOLD.id, option);
        const handleAssetArchivedThresholdChange = (option: string) =>
            updateAmpSetting(AMP_SETTINGS.tenant.ASSET_ARCHIVED_THRESHOLD.id, option);
        // Writes the full object back. Spreading the current value keeps a
        // threshold the tenant tuned elsewhere from being reset to the default.
        const handleMultipleDevicesToggle = (ev: ChangeEvent<HTMLInputElement>) =>
            updateAmpSetting(
                AMP_SETTINGS.tenant.MULTIPLE_DEVICES_SETTINGS.id,
                withMultipleDevicesEnabled(multipleDevicesSettings, ev.target.checked),
            );
        const handleWeekdayClick = (weekdayId: number) => {
            const updatedSelected = selectedWeekdays.includes(weekdayId)
                ? selectedWeekdays.filter((day) => day !== weekdayId)
                : [...selectedWeekdays, weekdayId];
            updateAmpSetting(AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_SELECTED_WEEKDAYS.id, updatedSelected);
        };
        const handleTimezoneChange = (timezone: string) =>
            updateAmpSetting(AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_TIMEZONE.id, timezone);
        const handleStartTimeChange = (time: string) =>
            updateAmpSetting(AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_START_TIME.id, time);
        const handleEndTimeChange = (time: string) =>
            updateAmpSetting(AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_END_TIME.id, time);
        const handleReminderAnchorRangeChange = (option: string) =>
            updateAmpSetting(REMINDER_ANCHOR_RANGE_SETTING.id, Number(option));

        const createAmpSetting = (settingId: string, value: any) => {
            if (tenantSettings && !Object.keys(tenantSettings)?.includes(settingId)) {
                const setting = (tenantSettings as any)[settingId];
                const modifiedValue =
                    settingId === AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_TIMEZONE.id
                        ? value.replace(/\(GMT[+-]\d{2}:\d{2}\)\s/, '')
                        : value;
                if (setting === undefined || setting === null) {
                    createSetting(settingId, modifiedValue)
                        .then(() => refreshTenantSettings())
                        .catch((err) => setError(err.message));
                }
            } else {
                const tempValue = (tenantSettings as { [key: string]: AmpSettingDto })?.[settingId]?.value as string;
                const settingValue =
                    settingId === AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_TIMEZONE.id
                        ? ((Array.isArray((window as any).__tzOpts) ? (window as any).__tzOpts : []).find(
                              (tz: string) => tz.toLowerCase().includes(tempValue?.toLowerCase()),
                          ) ?? tempValue)
                        : tempValue;
                AmpSettingSetter(settingId, settingValue);
            }
        };

        useEffect(() => {
            if (isLoading || !tenantSettings || Object.keys(tenantSettings).length === 0) {
                return;
            }
            [
                [
                    AMP_SETTINGS.tenant.ASSET_ARCHIVED_THRESHOLD.id,
                    AMP_SETTINGS.tenant.ASSET_ARCHIVED_THRESHOLD.defaultValue,
                ],
                [
                    AMP_SETTINGS.tenant.ASSET_LAST_SEEN_THRESHOLD.id,
                    AMP_SETTINGS.tenant.ASSET_LAST_SEEN_THRESHOLD.defaultValue,
                ],
                [
                    AMP_SETTINGS.tenant.MULTIPLE_DEVICES_SETTINGS.id,
                    AMP_SETTINGS.tenant.MULTIPLE_DEVICES_SETTINGS.defaultValue,
                ],
                [AMP_SETTINGS.tenant.WELCOME_MESSAGE_DELAY.id, AMP_SETTINGS.tenant.WELCOME_MESSAGE_DELAY.defaultValue],
                [
                    AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_SELECTED_WEEKDAYS.id,
                    AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_SELECTED_WEEKDAYS.defaultValue,
                ],
                [
                    AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_TIMEZONE.id,
                    AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_TIMEZONE.defaultValue,
                ],
                [
                    AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_START_TIME.id,
                    AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_START_TIME.defaultValue,
                ],
                [
                    AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_END_TIME.id,
                    AMP_SETTINGS.tenant.ACTIVE_ENGAGEMENT_WINDOW_END_TIME.defaultValue,
                ],
                [REMINDER_ANCHOR_RANGE_SETTING.id, REMINDER_ANCHOR_RANGE_SETTING.defaultValue],
                [AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.id, AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.defaultValue],
                [
                    AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.id,
                    AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.defaultValue,
                ],
            ].forEach(([id, def]) => createAmpSetting(id as string, def));
        }, [tenantSettings]);

        const dropdownTimeSlotsOptions = generateTimeSlots('12:00 AM', '11:30 PM', 30);

        // Optimistic toggle: update visually before API round-trip completes
        const [pendingNotifOn, setPendingNotifOn] = useState<boolean | null>(null);
        const notificationsOn = pendingNotifOn !== null ? pendingNotifOn : !enableBanner;
        // Once Redux reflects the confirmed API value, clear the optimistic override
        useEffect(() => {
            setPendingNotifOn(null);
        }, [enableBanner]);

        // New UI toggle is "Enable notifications" (checked = ON), so we pass
        // ev.target.checked directly — the old handler inverted it for the old "Disable" toggle.
        const handleNotifToggle = (ev: ChangeEvent<HTMLInputElement>) => {
            const newNotifOn = ev.target.checked;
            setPendingNotifOn(newNotifOn);
            handleToggle(AMP_SETTINGS.tenant.NOTIFICATIONS_ENABLED.id, newNotifOn);
        };

        const getTierLabel = (tier: string) => {
            if (tier === EARLY_ACCESS_ASSISTANT_TIER.STABLE) {
                return 'Stable';
            }
            if (tier.toLowerCase() === EARLY_ACCESS_ASSISTANT_TIER.DEV_USER_FRIENDLY?.toLowerCase()) {
                return 'Experimental';
            }
            return tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase();
        };

        const getTierDescription = (tier: string) => {
            if (tier.toLowerCase() === EARLY_ACCESS_ASSISTANT_TIER.DEV_USER_FRIENDLY.toLowerCase()) {
                return t(SETTINGS_EA_TIER_DESC_EXPERIMENTAL, 'Cutting-edge, changes frequently');
            }
            if (tier.toUpperCase() === EARLY_ACCESS_ASSISTANT_TIER.BETA) {
                return t(SETTINGS_EA_TIER_DESC_BETA, 'Stable with occasional issues');
            }
            return t(SETTINGS_EA_TIER_DESC_STABLE, 'General availability, fully stable');
        };

        // ─── Tab panels ────────────────────────────────────────────────────────
        const GeneralTab = () => (
            <div className="settings-v2__general-tab">
                {/* ── Stat summary row ─────────────────────────────────────── */}
                <div className="settings-v2__stat-row">
                    {/* ── Users & access nav card ──────────────────────── */}
                    <div
                        className="settings-v2__stat-card settings-v2__stat-card--nav"
                        role="button"
                        tabIndex={0}
                        onClick={() => setActiveTab('users')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                setActiveTab('users');
                            }
                        }}
                    >
                        <div className="settings-v2__nav-card-body">
                            <div className="settings-v2__stat-icon settings-v2__stat-icon--users-nav">
                                <RiUserLine size={28} />
                            </div>
                            <div className="settings-v2__nav-card-content">
                                <p className="settings-v2__nav-card-section-title">
                                    {t(SETTINGS_USERS_TITLE, 'Users & access')}
                                </p>
                                <div className="settings-v2__nav-stats">
                                    <div className="settings-v2__nav-stat-item">
                                        <span className="settings-v2__nav-stat-value">{totalAgents}</span>
                                        <span className="settings-v2__nav-stat-label">Total</span>
                                    </div>
                                    <div className="settings-v2__nav-stat-item">
                                        <span className="settings-v2__nav-stat-value settings-v2__nav-stat-value--active">
                                            {activeAgentsCount}
                                        </span>
                                        <span className="settings-v2__nav-stat-label">Active</span>
                                    </div>
                                    <div className="settings-v2__nav-stat-item">
                                        <span className="settings-v2__nav-stat-value settings-v2__nav-stat-value--admin">
                                            {adminCount}
                                        </span>
                                        <span className="settings-v2__nav-stat-label">Admins</span>
                                    </div>
                                    {pendingAgentsCount > 0 && (
                                        <div className="settings-v2__nav-stat-item">
                                            <span className="settings-v2__nav-stat-value settings-v2__nav-stat-value--pending">
                                                {pendingAgentsCount}
                                            </span>
                                            <span className="settings-v2__nav-stat-label">Pending</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <span className="settings-v2__stat-link">
                            {t(SETTINGS_NAV_USERS_LINK, 'Go to users & access →')}
                        </span>
                    </div>

                    {/* ── Health score nav card ─────────────────────────── */}
                    <div
                        className="settings-v2__stat-card settings-v2__stat-card--nav"
                        role="button"
                        tabIndex={0}
                        onClick={() => setActiveTab('health')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                setActiveTab('health');
                            }
                        }}
                    >
                        <div className="settings-v2__nav-card-body">
                            <div className="settings-v2__stat-icon settings-v2__stat-icon--health-nav">
                                <RiShieldLine size={28} />
                            </div>
                            <div className="settings-v2__nav-card-content">
                                <p className="settings-v2__nav-card-section-title">
                                    {t(SETTINGS_NAV_HEALTH_TITLE, 'Health score')}
                                </p>
                                <div className="settings-v2__nav-stats">
                                    <div className="settings-v2__nav-stat-item">
                                        <span className="settings-v2__nav-stat-value">
                                            {isFindingSpecsLoading ? (
                                                <Spinner
                                                    animation="border"
                                                    role="status"
                                                    className="settings-v2__kpi-spinner"
                                                    style={{
                                                        width: '1.25rem',
                                                        height: '1.25rem',
                                                        borderWidth: '0.15em',
                                                    }}
                                                />
                                            ) : (
                                                nonZeroDeductionCount
                                            )}
                                        </span>
                                        <span className="settings-v2__nav-stat-label">Deductions</span>
                                    </div>
                                    <div className="settings-v2__nav-stat-item">
                                        <span className="settings-v2__nav-stat-value">{highRiskCohortCount}</span>
                                        <span className="settings-v2__nav-stat-label">High risk cohorts</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <span className="settings-v2__stat-link">
                            {t(SETTINGS_NAV_HEALTH_LINK, 'Configure scores →')}
                        </span>
                    </div>

                    {/* ── Early access nav card ─────────────────────────── */}
                    <div
                        className="settings-v2__stat-card settings-v2__stat-card--nav"
                        role="button"
                        tabIndex={0}
                        onClick={() => setActiveTab('early')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                setActiveTab('early');
                            }
                        }}
                    >
                        <div className="settings-v2__nav-card-body">
                            <div className="settings-v2__stat-icon settings-v2__stat-icon--ea-nav">
                                <RiRocketLine size={28} />
                            </div>
                            <div className="settings-v2__nav-card-content">
                                <p className="settings-v2__nav-card-section-title">
                                    {t(SETTINGS_EA_TITLE, 'Early access')}
                                </p>
                                <div className="settings-v2__nav-ea-badges">
                                    <div className="settings-v2__nav-ea-row">
                                        <span className="settings-v2__nav-ea-section">Platform</span>
                                        <span
                                            className={`settings-v2__nav-ea-badge settings-v2__nav-ea-badge--${earlyAccessEnabled ? 'on' : 'off'}`}
                                        >
                                            {earlyAccessEnabled ? getTierLabel(selectedEarlyAccessTier) : 'Off'}
                                        </span>
                                    </div>
                                    <div className="settings-v2__nav-ea-row">
                                        <span className="settings-v2__nav-ea-section">Ampy</span>
                                        <span
                                            className={`settings-v2__nav-ea-badge settings-v2__nav-ea-badge--${ampyEarlyAccessEnabled ? 'on' : 'off'}`}
                                        >
                                            {ampyEarlyAccessEnabled ? getTierLabel(selectedAmpyEarlyAccessTier) : 'Off'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <span className="settings-v2__stat-link">{t(SETTINGS_NAV_EA_LINK, 'Explore features →')}</span>
                    </div>
                </div>

                {/* ── Config cards ──────────────────────────────────────────── */}
                <div className="settings-v2__cards-row">
                    {/* Engagement window */}
                    <div className="settings-v2__card settings-v2__card--engagement">
                        <div className="settings-v2__card-header">
                            <h3 className="settings-v2__card-title">
                                {t(SETTINGS_ENGAGEMENT_WINDOW_TITLE, 'Engagement window')}
                            </h3>
                            <p className="settings-v2__card-subtitle">
                                {t(SETTINGS_ENGAGEMENT_WINDOW_SUBTITLE, 'When notifications are sent')}
                            </p>
                        </div>

                        <div className="settings-v2__setting-row">
                            <div className="settings-v2__setting-label-group">
                                <p className="settings-v2__setting-label">
                                    {t(SETTINGS_WELCOME_DELAY_LABEL, 'Welcome message delay')}
                                </p>
                                <p className="settings-v2__setting-sublabel">
                                    {t(SETTINGS_WELCOME_DELAY_SUBLABEL, 'Wait before first notification')}
                                </p>
                            </div>
                            <div className="settings-v2__setting-control d-flex align-items-center">
                                <DynamicDropdown
                                    variant="default"
                                    options={welcomeMessageDropdownOptions}
                                    selectedOptions={selectedDelay}
                                    onOptionSelected={handleDelayChange}
                                />
                            </div>
                        </div>

                        <div className="settings-v2__setting-row settings-v2__weekday-row">
                            <div className="settings-v2__setting-label-group">
                                <p className="settings-v2__setting-label">
                                    {t(SETTINGS_ACTIVE_DAYS_LABEL, 'Active days')}
                                </p>
                                <p className="settings-v2__setting-sublabel">
                                    {t(SETTINGS_ACTIVE_DAYS_SUBLABEL, 'Mon – Fri active by default')}
                                </p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <WeekDayPicker
                                    selectedWeekdays={selectedWeekdays}
                                    onWeekdayClick={handleWeekdayClick}
                                />
                            </div>
                        </div>

                        <div className="settings-v2__setting-row">
                            <div className="settings-v2__setting-label-group">
                                <p className="settings-v2__setting-label">
                                    {t(SETTINGS_ACTIVE_HOURS_LABEL, 'Active hours')}
                                </p>
                                <p className="settings-v2__setting-sublabel">
                                    {t(SETTINGS_ACTIVE_HOURS_SUBLABEL, 'Engagement time window')}
                                </p>
                            </div>
                            <div className="settings-v2__time-range d-flex align-items-center">
                                <div className="settings-v2__time-control">
                                    <DynamicDropdown
                                        variant="default"
                                        options={dropdownTimeSlotsOptions}
                                        selectedOptions={startTime}
                                        onOptionSelected={handleStartTimeChange}
                                        placeholder={t(SETTINGS_START_TIME_PLACEHOLDER, 'Start Time')}
                                    />
                                </div>
                                <span className="settings-v2__time-sep">–</span>
                                <div className="settings-v2__time-control">
                                    <DynamicDropdown
                                        variant="default"
                                        options={dropdownTimeSlotsOptions}
                                        selectedOptions={endTime}
                                        onOptionSelected={handleEndTimeChange}
                                        placeholder={t(SETTINGS_END_TIME_PLACEHOLDER, 'End Time')}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="settings-v2__setting-row">
                            <div className="settings-v2__setting-label-group">
                                <p className="settings-v2__setting-label">{t(SETTINGS_TIMEZONE_LABEL, 'Timezone')}</p>
                                <p className="settings-v2__setting-sublabel">
                                    {t(SETTINGS_TIMEZONE_SUBLABEL, 'Used for all scheduling')}
                                </p>
                            </div>
                            <div className="settings-v2__setting-control d-flex align-items-center">
                                <EventTimeZoneSetting
                                    selectedTimezone={selectedTimezone}
                                    onTimezoneChange={handleTimezoneChange}
                                />
                            </div>
                        </div>

                        <div className="settings-v2__setting-row">
                            <div className="settings-v2__setting-label-group">
                                <p className="settings-v2__setting-label">
                                    {t(SETTINGS_REMINDER_RANGE_LABEL, 'AM/PM reminder range')}
                                </p>
                                <p className="settings-v2__setting-sublabel">
                                    {t(SETTINGS_REMINDER_RANGE_SUBLABEL, 'Anchoring window in minutes')}
                                </p>
                            </div>
                            <div className="settings-v2__setting-control d-flex align-items-center">
                                <DynamicDropdown
                                    variant="default"
                                    options={['30', '60', '90', '120', '150', '180', '240']}
                                    selectedOptions={reminderAnchorRangeMinutes}
                                    onOptionSelected={handleReminderAnchorRangeChange}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Notifications card */}
                    <div className="settings-v2__card settings-v2__card--notifications">
                        <div className="settings-v2__card-header">
                            <h3 className="settings-v2__card-title">
                                {t(SETTINGS_NOTIFICATIONS_TITLE, 'Engagements')}
                            </h3>
                            <p className="settings-v2__card-subtitle">
                                {t(SETTINGS_NOTIFICATIONS_SUBTITLE, 'Global kill switch')}
                            </p>
                        </div>

                        <div className="settings-v2__notif-toggle-row">
                            <p className="settings-v2__setting-label mb-0">
                                {t(SETTINGS_ENABLE_NOTIFICATIONS_LABEL, 'Enable engagements')}
                            </p>
                            <Form.Check
                                type="switch"
                                checked={notificationsOn}
                                onChange={handleNotifToggle}
                                className="settings-v2__toggle"
                            />
                        </div>

                        <div
                            className={`settings-v2__notif-status settings-v2__notif-status--${notificationsOn ? 'on' : 'off'}`}
                        >
                            {notificationsOn
                                ? t(
                                      SETTINGS_NOTIFICATIONS_ON_STATUS,
                                      'All engagements are active. Employees and agents are receiving updates.',
                                  )
                                : t(
                                      SETTINGS_NOTIFICATIONS_OFF_STATUS,
                                      'Engagements are globally disabled. Employees and agents will not receive updates.',
                                  )}
                        </div>
                    </div>

                    {/* Asset management card */}
                    <div className="settings-v2__card settings-v2__card--assets">
                        <div className="settings-v2__card-header">
                            <h3 className="settings-v2__card-title">{t('ASSET_MANAGEMENT', 'Asset management')}</h3>
                            <p className="settings-v2__card-subtitle">
                                {t(SETTINGS_ASSET_MGMT_SUBTITLE, 'Thresholds for asset status')}
                            </p>
                        </div>

                        <div className="settings-v2__setting-row">
                            <div className="settings-v2__setting-label-group">
                                <p className="settings-v2__setting-label">
                                    {t('ASSET_LAST_SEEN_THRESHOLD', 'Asset last seen threshold')}
                                </p>
                                <p className="settings-v2__setting-sublabel">
                                    {t(SETTINGS_ASSET_LAST_SEEN_SUBLABEL, 'Mark stale after inactivity')}
                                </p>
                            </div>
                            <div className="settings-v2__setting-control d-flex align-items-center">
                                <DynamicDropdown
                                    variant="default"
                                    options={generateDayOptions()}
                                    selectedOptions={assetLastSeenThreshold}
                                    onOptionSelected={handleAssetLastSeenThresholdChange}
                                />
                            </div>
                        </div>

                        <div className="settings-v2__setting-row">
                            <div className="settings-v2__setting-label-group">
                                <p className="settings-v2__setting-label">
                                    {t(SETTINGS_MULTIPLE_DEVICES_LABEL, 'Multiple devices per user')}
                                </p>
                                <p className="settings-v2__setting-sublabel">
                                    {t(
                                        SETTINGS_MULTIPLE_DEVICES_SUBLABEL,
                                        'Raise an informational finding when a user holds several devices',
                                    )}
                                </p>
                            </div>
                            <div className="settings-v2__setting-control d-flex align-items-center">
                                <Form.Check
                                    type="switch"
                                    checked={multipleDevicesSettings.enabled}
                                    onChange={handleMultipleDevicesToggle}
                                    className="settings-v2__toggle"
                                />
                            </div>
                        </div>

                        <div className="settings-v2__setting-row">
                            <div className="settings-v2__setting-label-group">
                                <p className="settings-v2__setting-label">
                                    {t(SETTINGS_IDLE_THRESHOLD_LABEL, 'Idle threshold')}
                                </p>
                                <p className="settings-v2__setting-sublabel">
                                    {t(SETTINGS_IDLE_THRESHOLD_SUBLABEL, 'Mark idle after no activity')}
                                </p>
                            </div>
                            <div className="settings-v2__setting-control d-flex align-items-center">
                                <DynamicDropdown
                                    variant="default"
                                    options={generateDayOptions()}
                                    selectedOptions={assetArchivedThreshold}
                                    onOptionSelected={handleAssetArchivedThresholdChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );

        const UsersTab = () => (
            <div className="settings-v2__users-tab">
                <div className="settings-v2__section-header">
                    <div className="settings-v2__section-icon">
                        <RiUserLine size={24} />
                    </div>
                    <div>
                        <h2 className="settings-v2__section-title">{t(SETTINGS_USERS_TITLE, 'Users & access')}</h2>
                        <p className="settings-v2__section-subtitle">
                            {t(
                                SETTINGS_USERS_SUBTITLE,
                                'Manage who can access this platform and their permission levels.',
                            )}
                        </p>
                    </div>
                </div>

                <div className="settings-v2__stat-row">
                    {[
                        {
                            icon: <RiGroupLine size={22} />,
                            value: totalAgents,
                            label: t(SETTINGS_USERS_STAT_TOTAL, 'Total users'),
                            sub: t(SETTINGS_USERS_STAT_TOTAL_SUB, 'Across all roles'),
                            mod: 'users',
                        },
                        {
                            icon: <RiGroupLine size={22} />,
                            value: activeAgentsCount,
                            label: t(SETTINGS_USERS_STAT_ACTIVE, 'Active users'),
                            sub: t(SETTINGS_USERS_STAT_ACTIVE_SUB, 'Currently active'),
                            mod: 'active',
                        },
                        {
                            icon: <RiGroupLine size={22} />,
                            value: pendingAgentsCount,
                            label: t(SETTINGS_USERS_STAT_PENDING, 'Pending invites'),
                            sub: t(SETTINGS_USERS_STAT_PENDING_SUB, 'Awaiting acceptance'),
                            mod: 'pending',
                        },
                        {
                            icon: <RiShieldLine size={22} />,
                            value: adminCount,
                            label: t(SETTINGS_USERS_STAT_ADMINS, 'Admins'),
                            sub: t(SETTINGS_USERS_STAT_ADMINS_SUB, 'Full access'),
                            mod: 'admin',
                        },
                    ].map((s) => (
                        <div key={s.label} className="settings-v2__stat-card settings-v2__stat-card--compact">
                            <div className={`settings-v2__stat-icon settings-v2__stat-icon--${s.mod}`}>{s.icon}</div>
                            <div>
                                <p className="settings-v2__stat-value">{s.value}</p>
                                <p className="settings-v2__stat-label">{s.label}</p>
                                <p className="settings-v2__stat-sub">{s.sub}</p>
                            </div>
                        </div>
                    ))}
                </div>

                <UserManagementGrid />

                <SsoSetupSection />
            </div>
        );

        const EarlyAccessFeatures = ({ features }: { features: string[] }) => (
            <div className="settings-v2__ea-features">
                {features.map((f) => (
                    <div key={f} className="settings-v2__ea-feature">
                        <RiCheckboxCircleFill size={16} />
                        <span>{f}</span>
                    </div>
                ))}
            </div>
        );

        const TIER_OPTIONS = [EARLY_ACCESS_ASSISTANT_TIER.BETA, EARLY_ACCESS_ASSISTANT_TIER.DEV_USER_FRIENDLY];

        const EarlyAccessTab = () => {
            if (isHidden) {
                return null;
            }

            const platformFeatures = [
                t(SETTINGS_EA_PLATFORM_FEATURE_1, 'Access new admin features before general release.'),
                t(SETTINGS_EA_PLATFORM_FEATURE_2, 'Help us test and improve upcoming capabilities.'),
                t(SETTINGS_EA_PLATFORM_FEATURE_3, 'Occasional issues may occur.'),
            ];
            const ampyFeatures = [
                t(SETTINGS_EA_AMPY_FEATURE_1, 'Try new chatbot capabilities and AI features.'),
                t(SETTINGS_EA_AMPY_FEATURE_2, 'Features may change frequently.'),
                t(SETTINGS_EA_AMPY_FEATURE_3, 'Share feedback to shape the experience.'),
            ];

            return (
                <div className="settings-v2__early-tab">
                    <div className="settings-v2__section-header">
                        <div className="settings-v2__section-icon">
                            <RiRocketLine size={24} />
                        </div>
                        <div>
                            <h2 className="settings-v2__section-title">{t(SETTINGS_EA_TITLE, 'Early access')}</h2>
                            <p className="settings-v2__section-subtitle">
                                {t(
                                    SETTINGS_EA_SUBTITLE,
                                    'Opt into beta or experimental features before general release.',
                                )}
                            </p>
                        </div>
                    </div>

                    <div className="settings-v2__info-banner">
                        <IoMdInformationCircleOutline size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                        {t(
                            SETTINGS_EA_INFO_BANNER,
                            'Early access features are in development and may change. Your feedback helps us improve.',
                        )}
                    </div>

                    <div className="settings-v2__ea-cards">
                        {/* Platform early access */}
                        <div className="settings-v2__ea-card">
                            <div className="settings-v2__ea-card-top">
                                <div>
                                    <h3 className="settings-v2__card-title">
                                        {t(SETTINGS_EA_PLATFORM_TITLE, 'Platform early access')}
                                    </h3>
                                    <p className="settings-v2__card-subtitle">
                                        {t(SETTINGS_EA_PLATFORM_SUBTITLE, 'Admin features & improvements')}
                                    </p>
                                </div>
                                <Form.Check
                                    type="switch"
                                    checked={earlyAccessEnabled}
                                    onChange={handleToggleEarlyAccessEnabled}
                                    className="settings-v2__toggle"
                                />
                            </div>

                            {earlyAccessEnabled && (
                                <div className="settings-v2__ea-tier">
                                    <div className="settings-v2__ea-tier-row">
                                        <div>
                                            <span className="settings-v2__ea-tier-label">
                                                {t(SETTINGS_EA_TIER_LABEL, 'Tier')}
                                            </span>
                                            <p className="settings-v2__ea-tier-desc">
                                                {getTierDescription(selectedEarlyAccessTier)}
                                            </p>
                                        </div>
                                        <div className="settings-v2__ea-tier-dropdown">
                                            <DynamicDropdown
                                                variant="default"
                                                options={TIER_OPTIONS.map(getTierLabel)}
                                                selectedOptions={getTierLabel(selectedEarlyAccessTier)}
                                                onOptionSelected={(label) => {
                                                    const tier = TIER_OPTIONS.find((t) => getTierLabel(t) === label);
                                                    if (tier) {
                                                        handleEarlyAccessTierChange(tier);
                                                    }
                                                }}
                                                isStartCase={false}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <EarlyAccessFeatures features={platformFeatures} />
                        </div>

                        {/* Ampy early access */}
                        <div className="settings-v2__ea-card">
                            <div className="settings-v2__ea-card-top">
                                <div>
                                    <h3 className="settings-v2__card-title">
                                        {t(SETTINGS_EA_AMPY_TITLE, 'Ampy early access')}
                                    </h3>
                                    <p className="settings-v2__card-subtitle">
                                        {t(SETTINGS_EA_AMPY_SUBTITLE, 'Chatbot capabilities')}
                                    </p>
                                </div>
                                <Form.Check
                                    type="switch"
                                    checked={ampyEarlyAccessEnabled}
                                    onChange={handleToggleAmpyEarlyAccessEnabled}
                                    className="settings-v2__toggle"
                                />
                            </div>

                            {ampyEarlyAccessEnabled && (
                                <div className="settings-v2__ea-tier">
                                    <div className="settings-v2__ea-tier-row">
                                        <div>
                                            <span className="settings-v2__ea-tier-label">
                                                {t(SETTINGS_EA_TIER_LABEL, 'Tier')}
                                            </span>
                                            <p className="settings-v2__ea-tier-desc">
                                                {getTierDescription(selectedAmpyEarlyAccessTier)}
                                            </p>
                                        </div>
                                        <div className="settings-v2__ea-tier-dropdown">
                                            <DynamicDropdown
                                                variant="default"
                                                options={TIER_OPTIONS.map(getTierLabel)}
                                                selectedOptions={getTierLabel(selectedAmpyEarlyAccessTier)}
                                                onOptionSelected={(label) => {
                                                    const tier = TIER_OPTIONS.find((t) => getTierLabel(t) === label);
                                                    if (tier) {
                                                        handleAmpyEarlyAccessTierChange(tier);
                                                    }
                                                }}
                                                isStartCase={false}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <EarlyAccessFeatures features={ampyFeatures} />
                        </div>
                    </div>
                </div>
            );
        };

        const TABS: { id: SettingsTab; label: string; hidden?: boolean }[] = [
            { id: 'general', label: t(SETTINGS_TAB_GENERAL, 'General') },
            { id: 'users', label: t(SETTINGS_TAB_USERS, 'Users & access'), hidden: isHidden },
            { id: 'health', label: t(SETTINGS_TAB_HEALTH, 'Health score') },
            { id: 'early', label: t(SETTINGS_TAB_EARLY_ACCESS, 'Early access'), hidden: isHidden },
            { id: 'mcp', label: t(SETTINGS_TAB_MCP, 'MCP access'), hidden: isMcpTabHidden },
        ];

        return (
            <div className="settings-v2">
                {/* ── Page header ──────────────────────────────────────────── */}
                <div className="settings-v2__header">
                    <h1 className="settings-v2__header-title">{t(SETTINGS_PAGE_TITLE, 'Settings')}</h1>
                    <p className="settings-v2__header-subtitle">
                        {t(SETTINGS_PAGE_SUBTITLE, 'Manage your platform configuration, users, and preferences.')}
                    </p>
                </div>

                {/* ── Tab navigation ───────────────────────────────────────── */}
                <div className="settings-v2__tab-bar">
                    {TABS.filter((tab) => !tab.hidden).map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            className={`settings-v2__tab${effectiveTab === tab.id ? ' settings-v2__tab--active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* ── Tab content ──────────────────────────────────────────── */}
                <div className="settings-v2__content custom-scrollbar">
                    {/*
                     * These tabs are defined inline in this render, so rendering them as JSX
                     * (<UsersTab />) gives them a new component identity on every re-render and
                     * makes React remount the whole subtree — resetting scroll and re-running
                     * child effects (e.g. SsoSetupSection's link fetch) on any state change such
                     * as a toast. Invoking them as functions inlines their output into this tree
                     * so children reconcile in place. None of these tabs call hooks.
                     */}
                    {effectiveTab === 'general' && GeneralTab()}
                    {effectiveTab === 'users' && UsersTab()}
                    {effectiveTab === 'health' && (
                        <SettingsHealthTab
                            activeConfigTab={healthConfigSubTab}
                            onConfigTabChange={setHealthConfigSubTab}
                        />
                    )}
                    {effectiveTab === 'early' && EarlyAccessTab()}
                    {effectiveTab === 'mcp' && <McpAccessSection />}
                </div>

                {/* ── Modals (unchanged) ───────────────────────────────────── */}
                <SettingConfirmationModal
                    content={modalContent()}
                    getAmpSettingValue={getAmpSettingValue}
                    savingSettings={savingSettings}
                    settingKey={settingKey!}
                    showConfirmationModal={showConfirmationModal}
                    title={modalTitle()}
                    onClose={handleModalClose}
                    onConfirm={handleConfirmModal}
                    onError={handleModalError}
                    onCallback={handleModalCallback}
                />
                <ConfirmationModal
                    modalTitle="CHANGE ROLE"
                    modalBodyTitle="Are you sure you want to change the role of this user?"
                    onPrimaryButtonClick={handleAccessChange}
                    primaryButtonLabel="CONFIRM"
                    show={showModal.accessUser}
                    onClose={handleCloseModal}
                    buttonVariant="primary"
                />
                <ConfirmationModal
                    modalTitle="REMOVE USER"
                    modalBodyTitle="Are you sure you want to remove this user?"
                    onPrimaryButtonClick={handleRemoveUser}
                    primaryButtonLabel="YES, REMOVE IT"
                    show={showModal.removeUser}
                    onClose={handleCloseModal}
                    buttonVariant="danger"
                />
            </div>
        );
    };

    return (
        <PageErrorHandlerAndLoaderWrapper
            error={settingsError}
            render={SettingsContent}
            isLoading={isLoading}
            handleRefresh={refreshTenantSettings}
        />
    );
};

export default SettingsPage;
